import { useEffect, useId, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from 'lucide-react';
import { ProductImage } from '../../ui/ProductImage';
import { HeadingText } from '../shared/HeadingText';
import { AddButton } from '../shared/AddButton';
import { useCatalog } from '../../contexts/CatalogContext';
import { useCartActions, useOverlayActions } from '../../contexts/StoreContext';
import { usePages, useSite } from '../../contexts/SiteContext';
import { tierIn } from '../../data/gifts';
import { shopLabels } from '../../data/shopLabels';
import { euro, rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { ease } from '../../core/format/motion';
import { isAllSoldOut, isSoldOut } from '../../core/rules/stock';
import { treatmentDuration, treatmentPrice } from '../../utils/catalog';
import { fill } from '../../utils/copy';
import { belowHeader, eyebrowCls, quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { btnIcon, btnOnClay, btnPrice, btnPriceAmount, btnPriceLabel, chipLgCls } from '../../ui/styles';
import { capList } from './homeRules';
import { pageActions } from '../../data/pageActions';
import type { Concern, Routine } from '../../core/types/catalog';

/** Ruimte tussen de panelen (Tailwind `gap-6`). */
const GAP = 24;

/**
 * "Waar loopt jouw huid tegenaan?" (stap Kiezen): per klacht beide kanten van Beauty Bewust.
 * Links wat je thuis gebruikt (het protocol, los of in één keer in de mand), rechts wat er in de
 * salon bij past.
 *
 * De klachten zijn tabs bovenin (blijven vast staan zolang je in het blok bent); de adviezen
 * staan als panelen naast elkaar en klikken per paneel vast. Swipen (telefoon, tablet,
 * trackpad) of op een pil tikken: allebei schuiven het paneel en de pil mee. Geen half
 * zichtbare buren: de panelen zijn hoog. De Thuis-lijst is hoogstens vier regels, dus alle
 * panelen zijn even hoog en niets verspringt. Pijltjestoetsen wisselen ook.
 */
export function ConcernFinder() {
  const pages = usePages();
  const {
    concerns,
    routineForConcern,
    shopRating
  } = useCatalog();
  const {
    openQuiz
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const baseId = useId();
  const copy = pages.home.concerns;
  const [active, setActive] = useState(0);

  // Alleen klachten met een protocol: zonder is er niets te kiezen (lege backend).
  const items = concerns.flatMap(c => {
    const routine = routineForConcern(c);
    return routine ? [{
      concern: c,
      routine
    }] : [];
  });
  const trackRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const pillRowRef = useRef<HTMLUListElement>(null);
  const pillRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const overflow = usePillOverflow(pillRowRef, items.length);

  // De actieve pil altijd in beeld in zijn rij (alleen horizontaal: de pagina scrolt niet mee).
  useEffect(() => {
    const row = pillRowRef.current;
    const pill = pillRefs.current[active];
    if (!row || !pill) return;
    const left = pill.offsetLeft - row.clientWidth / 2 + pill.clientWidth / 2;
    row.scrollTo({
      left: Math.max(0, left),
      behavior: reduce ? 'auto' : 'smooth'
    });
  }, [active, reduce]);
  if (items.length === 0) return null;

  /** Na swipen: welk paneel staat er nu? */
  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const next = Math.round(track.scrollLeft / (track.clientWidth + GAP));
    if (next !== active && next >= 0 && next < items.length) setActive(next);
  };

  /** Pil, pijl of toets: naar paneel `i`. Viel het advies onder de vaste balk, dan terug naar boven. */
  const choose = (i: number, focus = false) => {
    const index = Math.max(0, Math.min(items.length - 1, i));
    setActive(index);
    const track = trackRef.current;
    if (track) track.scrollTo({
      left: index * (track.clientWidth + GAP),
      behavior: reduce ? 'auto' : 'smooth'
    });
    if (focus) pillRefs.current[index]?.focus();
    const bar = barRef.current;
    if (!bar || !track) return;
    const gap = track.getBoundingClientRect().top - bar.getBoundingClientRect().bottom;
    if (gap < -1) window.scrollBy({
      top: gap - 8,
      behavior: reduce ? 'auto' : 'smooth'
    });
  };
  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const keys: Record<string, number> = {
      ArrowRight: active + 1,
      ArrowLeft: active - 1,
      Home: 0,
      End: items.length - 1
    };
    const target = keys[e.key];
    if (target === undefined) return;
    e.preventDefault();
    choose(target, true);
  };
  return <Section tone="shell" labelledBy="concern" contained={false} data-id="element-7278" data-mp="br1w0qx428pj">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-7279" data-mp="bruu73i07pw2">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-7280" data-mp="2igpyar86symx">
          <div className="lg:col-span-7" data-id="element-7281" data-mp="2dioeg42xsvae">
            {copy.eyebrow && <p className={`${eyebrowCls} text-clay`} data-id="element-7282" data-mp="2dk9ajy4wgmad">{fill(copy.eyebrow)}</p>}
            <h2 id="concern" className={`${copy.eyebrow ? 'mt-4' : ''} ${sectionHeading} text-ink`} data-id="element-7283" data-mp="t1ozgcesuf0c">
              <HeadingText heading={copy.heading} data-id="element-7284" data-mp="xy5or9xqizf0" />
            </h2>
          </div>
          <div className="lg:col-span-5" data-id="element-7285" data-mp="k8vepzgl2nf3">
            {copy.body && <p className="max-w-md text-[17px] leading-relaxed text-muted" data-id="element-7286" data-mp="k698q49ccfyz">{fill(copy.body)}</p>}
            {copy.quizLine && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={`mt-2 ${quietLink} text-left text-ink underline decoration-ink/25 hover:decoration-ink`} data-id="element-7287" data-mp="a9cwpok8hbpo">
                {fill(copy.quizLine)}
              </button>}
          </div>
        </div>

        {/* De klachten blijven net onder de header staan zolang je door dit blok scrolt. */}
        <div ref={barRef} className={`sticky ${belowHeader} z-sticky -mx-6 mt-10 flex items-center gap-2 bg-shell py-3 lg:-mx-10 lg:px-10`} data-id="element-7288" data-mp="1vl4m7mw94bdy">
          <ul ref={pillRowRef} role="tablist" aria-label="Kies je huidklacht" onKeyDown={onKeyDown} className="flex min-w-0 flex-1 gap-2 overflow-x-auto scroll-px-6 px-6 pb-1 [scrollbar-width:none] lg:px-0 [&::-webkit-scrollbar]:hidden" data-id="element-7289" data-mp="38mamgk4mrwte">
            {items.map(({
            concern
          }, i) => {
            const isActive = i === active;
            return <li key={concern.id} role="presentation" className="shrink-0" data-id="element-7290" data-mp="3raz3guzzthzw" data-mpi={i}>
                  <button ref={el => {
                pillRefs.current[i] = el;
              }} type="button" role="tab" id={`${baseId}-tab-${i}`} aria-selected={isActive} aria-controls={`${baseId}-panel-${i}`} tabIndex={isActive ? 0 : -1} onClick={() => choose(i)} className={chipLgCls(isActive)} data-id="element-7291" data-mp="3naf6psjc0gea">
                    {isActive && <motion.span layoutId="concern-pill" aria-hidden="true" className="absolute inset-[-1px] rounded-full bg-ink" transition={{
                  duration: reduce ? 0 : 0.25,
                  ease
                }} data-id="element-7292" data-mp="hhedsjds51cd" />}
                    <span className="relative" data-id="element-7293" data-mp="10efh7zroqte7">{concern.label}</span>
                  </button>
                </li>;
          })}
          </ul>
          {/* Vorige/volgende alleen als de pillen niet allemaal passen (computer zonder trackpad). */}
          {overflow && <div className="hidden shrink-0 gap-1 pr-6 sm:flex lg:pr-0" data-id="element-7294" data-mp="1f327uxmrj7kq">
              <ArrowButton label="Vorige klacht" disabled={active === 0} onClick={() => choose(active - 1)} icon="prev" data-id="element-7295" data-mp="1f28v9g14hnuo" />
              <ArrowButton label="Volgende klacht" disabled={active === items.length - 1} onClick={() => choose(active + 1)} icon="next" data-id="element-7296" data-mp="1j2swovsqbdmm" />
            </div>}
        </div>

        {/* Het spoor: alle panelen naast elkaar, vastklikken per paneel. Even hoog door de vaste lijst. */}
        <motion.div ref={trackRef} onScroll={onScroll} initial={reduce ? false : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '0px 0px -15% 0px'
      }} transition={{
        duration: 0.3,
        ease
      }} className="mt-5 flex snap-x snap-mandatory gap-6 overflow-x-auto overscroll-x-contain [scrollbar-width:none] lg:mt-8 [&::-webkit-scrollbar]:hidden" data-id="element-7297" data-mp="24hyfaal1hqf9">
          {items.map(({
          concern,
          routine
        }, i) => <ConcernPanel key={concern.id} id={`${baseId}-panel-${i}`} labelledBy={`${baseId}-tab-${i}`} concern={concern} routine={routine} hidden={i !== active} ratingLine={shopRating.count > 0 ? `${rating(shopRating.average)} uit ${plural(shopRating.count, 'beoordeling', 'beoordelingen')}` : ''} data-id="element-7298" data-mp="1vnaccr2xsktm" data-mpi={i} />)}
        </motion.div>
      </div>
    </Section>;
}
type PanelProps = {
  id: string;
  labelledBy: string;
  concern: Concern;
  routine: Routine;
  hidden: boolean;
  ratingLine: string;
};

/** Eén advies: Thuis (het protocol) en In de salon (de behandeling of het consult). */
function ConcernPanel({
  id,
  labelledBy,
  concern,
  routine,
  hidden,
  ratingLine
}: PanelProps) {
  const {
    routineTotals,
    productsForSkinType,
    treatmentForConcern
  } = useCatalog();
  const {
    addRoutine
  } = useCartActions();
  const {
    openBooking
  } = useOverlayActions();
  const {
    giftTiers,
    policy
  } = useSite();
  const addCta = usePages().home.concerns.addCta || 'Protocol in de mand';
  const treatment = treatmentForConcern(concern);
  const totals = routineTotals(routine);
  const matching = productsForSkinType(concern.skinType).length;
  const gift = tierIn(giftTiers, totals.price);
  const allSoldOut = isAllSoldOut(totals.items);
  const {
    shown,
    more
  } = capList(totals.items);
  return <div id={id} role="tabpanel" aria-labelledby={labelledBy} aria-hidden={hidden}
  // Niet-actieve panelen: niet bereikbaar met Tab, wel zichtbaar tijdens het swipen.
  ref={el => {
    if (!el) return;
    if (hidden) el.setAttribute('inert', '');else el.removeAttribute('inert');
  }} className="flex w-full shrink-0 snap-start flex-col" data-id="element-7299" data-mp="3o38d11hb6tdq">
      <p className="max-w-3xl text-[17px] leading-relaxed text-muted" data-id="element-7300" data-mp="35d0gx3c11371">{concern.description}</p>

      <div className="mt-8 grid flex-1 gap-4 lg:grid-cols-12 lg:gap-6" data-id="element-7301" data-mp="3a7nxmg0sgtb2">
        {/* Thuis */}
        <article aria-label={`Thuis bij ${concern.label.toLowerCase()}`} className="flex flex-col bg-sand p-6 sm:p-8 lg:col-span-7 lg:p-10" data-id="element-7302" data-mp="2wc2bqhpuqpmv">
          <p className="text-[13px] text-muted" data-id="element-7303" data-mp="ui2wjl9lzo4w">Thuis</p>
          <h3 className="mt-1 type-title text-ink" data-id="element-7304" data-mp="ugi2dwr7i77k">{routine.name}</h3>

          <ul className="mt-6 divide-y divide-line border-y border-line" data-id="element-7305" data-mp="yiwkgen1x2xs">
            {shown.map(p => <li key={p.id} className="flex items-center gap-3 py-3" data-id="element-7306" data-mp="2j2s40dhjl659">
                <ProductImage src={p.image} alt="" width={48} className="h-14 w-12 shrink-0 p-1" data-id="element-7307" data-mp="2f1oltkc0uy0v" />
                <div className="min-w-0 flex-1" data-id="element-7308" data-mp="2st3gwpj7epoa">
                  <p className="text-[12px] text-clay" data-id="element-7309" data-mp="2stwg95fu2cwe">{isSoldOut(p) ? shopLabels.soldOutShort : p.step}</p>
                  <Link to={`/webshop/${p.id}`} className="text-[15px] leading-snug text-ink transition-colors duration-150 ease-soft hover:text-clay" data-id="element-7310" data-mp="32q7tbxn8z6rw">
                    {p.name}
                  </Link>
                </div>
                <span className="shrink-0 text-[14px] tabular-nums text-muted" data-id="element-7311" data-mp="3brfo45qk1ph8">{euro(p.price)}</span>
                <AddButton product={p} data-id="element-7312" data-mp="36rl0cb22tg0e" />
              </li>)}
          </ul>
          {more > 0 && <p className="mt-3 text-[14px] text-muted" data-id="element-7313" data-mp="1hc3ev2720wh2">+ {more} meer in het protocol</p>}

          <div className="mt-auto pt-7" data-id="element-7314" data-mp="1cebnqae8hedx">
            {!allSoldOut && <button type="button" onClick={() => addRoutine(routine.id)} className={`${btnPrice} sm:w-auto sm:min-w-[20rem]`} data-id="element-7315" data-mp="1cg6alu92fgkn">
                <span className={btnPriceLabel} data-id="element-7316" data-mp="3hemgsiylhg8c">{fill(addCta)}</span>
                <span className={btnPriceAmount} data-id="element-7317" data-mp="3hgz65h6omsvf">{euro(totals.price)}</span>
              </button>}
            {/* Bewijs op het moment van kiezen. */}
            {ratingLine && <p className="mt-3 flex items-center gap-1.5 text-[13px] text-ink" data-id="element-7318" data-mp="3qdhnrx9gqucp">
                <StarIcon className="h-3.5 w-3.5 fill-current text-clay" strokeWidth={0} aria-hidden="true" data-id="element-7319" data-mp="3veniu1qlitpe" />
                {ratingLine}
              </p>}
            <p className="mt-2 text-[13px] leading-relaxed text-muted" data-id="element-7320" data-mp="90dvlvyejrq7">
              Gaat {totals.months} mee · met je huidplan in Mijn omgeving
              {gift ? ` · ${gift.title} ligt voor je klaar` : ''}
              {matching > 0 && <>
                  {' · '}
                  <Link to={`/webshop?huid=${encodeURIComponent(concern.skinType)}`} className="underline underline-offset-4 hover:text-ink" data-id="element-7321" data-mp="n05g8fjfc6d6">
                    alle {plural(matching, 'product', 'producten')} voor {concern.skinType.toLowerCase()}
                  </Link>
                </>}
            </p>
          </div>
        </article>

        {/* In de salon: klei, alleen shell-tekst (5,6:1). Niet uitrekken: geen leeg klei-vlak onderaan. */}
        <article aria-label={`In de salon bij ${concern.label.toLowerCase()}`} className="flex flex-col bg-clay p-6 text-shell sm:p-8 lg:col-span-5 lg:self-start lg:p-10" data-id="element-7322" data-mp="2q7mi3v5pqo0o">
          <p className="text-[13px]" data-id="element-7323" data-mp="2hf2hwq9rwbdq">In de salon</p>
          {treatment ? <>
              <h3 className="mt-1 type-title" data-id="element-7324" data-mp="2cg92gsaxeaax">{treatment.name}</h3>
              {treatment.tagline && <p className="mt-4 text-[17px] leading-relaxed" data-id="element-7325" data-mp="2ceeq42dpzqig">{treatment.tagline}</p>}
              <dl className="mt-6 grid grid-cols-2 border-y border-shell/25 text-[14px]" data-id="element-7326" data-mp="39xpzvwth3aal">
                <div className="py-3 pr-4" data-id="element-7327" data-mp="353lqz80ncs6v">
                  <dt className="text-[12px]" data-id="element-7328" data-mp="2oow3j482p26y">Duur</dt>
                  <dd className="mt-0.5 type-name" data-id="element-7329" data-mp="2oo2v7ytgu4mp">{treatmentDuration(treatment)}</dd>
                </div>
                <div className="border-l border-shell/25 py-3 pl-4" data-id="element-7330" data-mp="2tme2q2wag31r">
                  <dt className="text-[12px]" data-id="element-7331" data-mp="2fq0tti5tl9yw">Prijs</dt>
                  <dd className="mt-0.5 type-name" data-id="element-7332" data-mp="2kniu6k5rugva">{treatmentPrice(treatment)}</dd>
                </div>
              </dl>
            </> : <>
              <h3 className="mt-1 type-title" data-id="element-7333" data-mp="38fhjw95ywery">Liever eerst laten kijken?</h3>
              <p className="mt-4 text-[17px] leading-relaxed" data-id="element-7334" data-mp="3cegnnqfyq5ht">
                In een {policy.consult.name.toLowerCase()} van {policy.consult.minutes} minuten kijk ik met je mee en stel ik je plan op.
              </p>
            </>}
          <div className="pt-7" data-id="element-7335" data-mp="3rc920f9rwnuf">
            <button type="button" onClick={() => openBooking(treatment?.slug ?? policy.consult.slug)} aria-haspopup="dialog" className={btnOnClay} data-id="element-7336" data-mp="3rb7o831dhn90">
              {pageActions.book}
              <span className="sr-only" data-id="element-7337" data-mp="9bv8wvmu61l2">: {treatment ? treatment.name : policy.consult.name}</span>
            </button>
          </div>
        </article>
      </div>
    </div>;
}
function ArrowButton({
  label,
  disabled,
  onClick,
  icon
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  icon: 'prev' | 'next';
}) {
  const Icon = icon === 'prev' ? ChevronLeftIcon : ChevronRightIcon;
  return <button type="button" aria-label={label} disabled={disabled} onClick={onClick} className={btnIcon} data-id="element-7338" data-mp="3midk0yo58q5h">
      <Icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7339" data-mp="3iiwds30w7oz9" />
    </button>;
}

/** Passen de pillen niet in hun rij? Dan tonen we vorige/volgende ernaast. */
function usePillOverflow(ref: RefObject<HTMLElement | null>, count: number): boolean {
  const [overflow, setOverflow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setOverflow(el.scrollWidth > el.clientWidth + 1);
    check();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, count]);
  return overflow;
}